import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link, withRouter } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useQuery, useMutation } from "react-apollo-hooks";
import { toast } from "react-toastify";
import {
  MANAGER_FEE_DETAIL_QUERY,
  MANAGER_FEE_EDIT_QUERY,
} from "../../Queries";
import { Panel, PanelHeader, PanelBody } from "../../../../Components/Panel/Panel";
import { graphqlUri } from "../../../../Config/Env";
import DefaultRangeHelp from "../../../../Components/Fee/DefaultRangeHelp";

export default withRouter(({ match, location, history }) => {
  const feeId = match.params.feeId;

  const { data, loading } = useQuery(MANAGER_FEE_DETAIL_QUERY, {
    variables: { id: feeId },
    fetchPolicy: "network-only",
  });

  if (loading || !data || !data.ManagerFeeDetail) {
    return (
      <div className="panel-loader">
        <span className="spinner-small"></span>
      </div>
    );
  }

  return (
    <FeeEditForm
      fee={data.ManagerFeeDetail}
      feeId={feeId}
      location={location}
      history={history}
    />
  );
});

const FeeEditForm = ({ fee, feeId, location, history }) => {
  const [feeEditMutation] = useMutation(MANAGER_FEE_EDIT_QUERY, {
    refetchQueries: () => [
      { query: MANAGER_FEE_DETAIL_QUERY, variables: { id: feeId } },
    ],
  });

  const { register, handleSubmit, errors, watch } = useForm({
    defaultValues: {
      title: fee.title,
      contents: fee.contents,
      planner: fee.planner,
      plannerUseSNS: fee.plannerUseSNS,
      plannerSNS: fee.plannerSNS,
      isDefault: fee.isDefault,
      defaultRange: fee.defaultRange != null ? String(fee.defaultRange) : "0",
    },
  });

  const [feeCoverImg, setFeeCoverImg] = useState(
    fee.coverImg
      ? graphqlUri + fee.coverImg
      : "/assets/img/gallery/gallery-default.jpg"
  );
  const [feeDetailImg, setFeeDetailImg] = useState(
    fee.detailImg
      ? graphqlUri + fee.detailImg
      : "/assets/img/gallery/gallery-default.jpg"
  );
  const handleFeeCoverImg = (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setFeeCoverImg(reader.result);
  };
  const handleFeeDetailImg = (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setFeeDetailImg(reader.result);
  };

  const onSubmit = async (form) => {
    try {
      let variables = {
        id: feeId,
        title: form.title,
        contents: form.contents,
        planner: form.planner,
        plannerUseSNS: form.plannerUseSNS,
        plannerSNS: form.plannerUseSNS === "S" ? "-" : form.plannerSNS,
        isDefault: form.isDefault,
        defaultRange:
          form.isDefault === "true" ? Number(form.defaultRange) : 0,
      };
      if (form.coverImg[0]) {
        variables = { ...variables, coverImg: form.coverImg[0] };
      }
      if (form.detailImg[0]) {
        variables = { ...variables, detailImg: form.detailImg[0] };
      }

      const {
        data: { ManagerFeeEdit },
      } = await feeEditMutation({ variables });
      if (ManagerFeeEdit) {
        toast.success("수정 되었습니다.");
        history.push(`/manager/fees${location.search}`);
      } else {
        toast.error("수정에 실패했습니다.");
      }
    } catch (e) {
      const msg = e.toString().replace("Error: GraphQL error:", "").trim();
      toast.error(msg || "에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  return (
    <>
      <Helmet>
        <title>회비 수정 | 한밭페이</title>
      </Helmet>
      <form className="margin-bottom-0" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <ol className="breadcrumb float-xl-right">
            <li className="breadcrumb-item">
              <Link to={`/manager/fees${location.search}`}>담당 회비</Link>
            </li>
            <li className="breadcrumb-item active">회비 수정</li>
          </ol>
          <h1 className="page-header">회비 수정</h1>

          <div className="row m-b-15">
            <div className="col-xs-6 text-left">
              <Link
                to={`/manager/fees${location.search}`}
                className="btn btn-white"
              >
                <i className="fa fa-list m-r-5"></i>목록
              </Link>
            </div>
            <div className="col-xs-6 text-right">
              <button type="submit" className="btn btn-white text-success">
                <i className="fa fa-check m-r-5"></i>수정
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 col-sm-12">
              <Panel>
                <PanelHeader noButton={true}>수정</PanelHeader>
                <PanelBody className="panel-form">
                  <div className="form-horizontal form-bordered">
                    <div className="form-group row">
                      <label className="col-form-label col-md-3">주관</label>
                      <div className="col-md-9">
                        <input
                          name="planner"
                          type="text"
                          className="form-control m-b-5"
                          placeholder="주관 입력"
                          ref={register({ required: "주관을 입력해주세요." })}
                        />
                        {errors.planner && (
                          <span>{errors.planner.message}</span>
                        )}
                      </div>
                    </div>
                    <div className="form-group row">
                      <label className="col-form-label col-md-3">
                        SNS 사용 여부
                      </label>
                      <div className="col-md-9">
                        <div className="radio radio-css radio-inline">
                          <input
                            type="radio"
                            id="snsUseC"
                            name="plannerUseSNS"
                            className="form-control"
                            value="C"
                            ref={register({ required: "선택해주세요." })}
                          />
                          <label htmlFor="snsUseC">사용</label>
                        </div>
                        <div className="radio radio-css radio-inline">
                          <input
                            type="radio"
                            id="snsUseS"
                            name="plannerUseSNS"
                            className="form-control"
                            value="S"
                            ref={register({ required: "선택해주세요." })}
                          />
                          <label htmlFor="snsUseS">미사용</label>
                        </div>
                        {errors.plannerUseSNS && (
                          <div>
                            <span>{errors.plannerUseSNS.message}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {watch("plannerUseSNS") === "C" && (
                      <div className="form-group row">
                        <label className="col-form-label col-md-3">
                          주관 SNS
                        </label>
                        <div className="col-md-9">
                          <input
                            name="plannerSNS"
                            type="text"
                            className="form-control m-b-5"
                            placeholder="주관 SNS 입력"
                            ref={register({
                              validate: (value) =>
                                watch("plannerUseSNS") === "C" && value === ""
                                  ? "주관 SNS를 입력해주세요."
                                  : undefined,
                            })}
                          />
                          {errors.plannerSNS && (
                            <span>{errors.plannerSNS.message}</span>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="form-group row">
                      <label className="col-form-label col-md-3">
                        미납 사용 여부
                      </label>
                      <div className="col-md-9">
                        <div className="radio radio-css radio-inline">
                          <input
                            type="radio"
                            id="isDefaultTrue"
                            name="isDefault"
                            className="form-control"
                            value="true"
                            ref={register({
                              required: "미납 사용 여부를 선택해주세요.",
                            })}
                          />
                          <label htmlFor="isDefaultTrue">사용</label>
                        </div>
                        <div className="radio radio-css radio-inline">
                          <input
                            type="radio"
                            id="isDefaultFalse"
                            name="isDefault"
                            className="form-control"
                            value="false"
                            ref={register({
                              required: "미납 사용 여부를 선택해주세요.",
                            })}
                          />
                          <label htmlFor="isDefaultFalse">미사용</label>
                        </div>
                        {errors.isDefault && (
                          <div>
                            <span>{errors.isDefault.message}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {watch("isDefault") === "true" && (
                      <div className="form-group row">
                        <label className="col-form-label col-md-3">
                          미납 징수 범위
                        </label>
                        <div className="col-md-9">
                          <select
                            name="defaultRange"
                            className="form-control"
                            style={{ maxWidth: 240 }}
                            ref={register()}
                          >
                            <option value="0">전체</option>
                            <option value="1">1개월</option>
                            <option value="2">2개월</option>
                            <option value="3">3개월</option>
                          </select>
                          <DefaultRangeHelp />
                        </div>
                      </div>
                    )}
                    <div className="form-group row">
                      <label className="col-form-label col-md-3">제목</label>
                      <div className="col-md-9">
                        <input
                          type="text"
                          name="title"
                          className="form-control m-b-5"
                          placeholder="제목 입력"
                          ref={register({ required: "제목을 입력해주세요." })}
                        />
                        {errors.title && <span>{errors.title.message}</span>}
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-xs-12">
                        <textarea
                          name="contents"
                          className="form-control m-b-5"
                          placeholder="내용 입력"
                          ref={register({ required: "내용을 입력해주세요." })}
                          rows="10"
                        ></textarea>
                        {errors.contents && (
                          <span>{errors.contents.message}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </PanelBody>
              </Panel>
            </div>

            <div className="col-md-3 col-sm-12">
              <Panel>
                <PanelHeader noButton={true}>커버 이미지</PanelHeader>
                <PanelBody>
                  <div className="bg-gray-transparent-1 rounded">
                    <div
                      className="coverImg"
                      style={{ backgroundImage: `url(${feeCoverImg})` }}
                    >
                      <img
                        src={feeCoverImg}
                        alt="이미지 업로드"
                        className="sr-only"
                      />
                    </div>
                  </div>
                  <div className="m-t-10">
                    <input
                      type="file"
                      id="coverImg"
                      name="coverImg"
                      className="hide"
                      onChange={handleFeeCoverImg}
                      ref={register({})}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-block"
                      onClick={() => document.getElementById("coverImg").click()}
                    >
                      커버 이미지 업로드
                    </button>
                  </div>
                  <div className="alert alert-primary fade show m-t-10 m-b-0">
                    <b>커버이미지 사이즈 :</b> 넓이 1200px X 높이 1024px
                  </div>
                </PanelBody>
              </Panel>
            </div>

            <div className="col-md-3 col-sm-12">
              <Panel>
                <PanelHeader noButton={true}>상세 이미지</PanelHeader>
                <PanelBody>
                  <div className="bg-gray-transparent-1 rounded">
                    <div
                      className="coverImg"
                      style={{ backgroundImage: `url(${feeDetailImg})` }}
                    >
                      <img
                        src={feeDetailImg}
                        alt="이미지 업로드"
                        className="sr-only"
                      />
                    </div>
                  </div>
                  <div className="m-t-10">
                    <input
                      type="file"
                      id="detailImg"
                      name="detailImg"
                      className="hide"
                      onChange={handleFeeDetailImg}
                      ref={register({})}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-block"
                      onClick={() =>
                        document.getElementById("detailImg").click()
                      }
                    >
                      상세 이미지 업로드
                    </button>
                  </div>
                  <div className="alert alert-primary fade show m-t-10 m-b-0">
                    <b>이미지 사이즈 :</b> 넓이 1200px X 높이 1200px
                  </div>
                </PanelBody>
              </Panel>
            </div>
          </div>

          <div className="row">
            <div className="col-xs-6 text-left">
              <Link
                to={`/manager/fees${location.search}`}
                className="btn btn-white"
              >
                <i className="fa fa-list m-r-5"></i>목록
              </Link>
            </div>
            <div className="col-xs-6 text-right">
              <button type="submit" className="btn btn-white text-success">
                <i className="fa fa-check m-r-5"></i>수정
              </button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
